"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  Suspense,
} from "react";

import { Loader } from "@/dashboard/components/ui/loader";
import { setActiveTenantIdForFetch } from "@/dashboard/hooks/api/fetch-json";
import {
  useMeQuery,
  type SpoofRealUser,
  type SpoofError,
} from "@/dashboard/hooks/auth/use-me-query";
import { useSignIn } from "@/dashboard/hooks/auth/use-sign-in";
import { useSignOut } from "@/dashboard/hooks/auth/use-sign-out";
import { useSelectTenant } from "@/dashboard/hooks/tenant/use-select-tenant";
import { isHttpError } from "@/dashboard/lib/api/errors";
import { ALL_TENANTS_ID } from "@/dashboard/lib/auth/constants";
import { canAny, getRoleForTenant } from "@/dashboard/lib/auth/role-helpers";
import type { Tenant } from "@/dashboard/types/tenant";
import type { User } from "@/dashboard/types/user";

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */
type LogoutReason = "manual" | "session-expired" | null;

interface AuthContextShape {
  user: User | null;
  tenants: Tenant[];
  /**
   * null → auth still loading
   * "ALL" → multi-tenant mode
   * uuid → single-tenant mode
   */
  activeTenantId: string | null;
  authResolved: boolean;
  isAuthenticated: boolean;
  isSwitchingTenant: boolean;
  isSelfServe: boolean;
  isAccountOverride: boolean;
  /** True when the deployment Host is a demo subdomain (see /api/dashboard/session). */
  piiDemoBlinding: boolean;
  isNonDashboardUser: boolean;
  isAnalyticsViewer: boolean;
  isSpoofing: boolean;
  realUser: SpoofRealUser | null;
  spoofError: SpoofError | null;
  logoutReason: LogoutReason;

  /* actions */
  signIn: (
    args: Parameters<ReturnType<typeof useSignIn>["mutateAsync"]>[0]
  ) => ReturnType<ReturnType<typeof useSignIn>["mutateAsync"]>;
  signOut: () => Promise<unknown>;
  switchTenant: (id: string, opts?: { redirectTo?: string; refresh?: boolean }) => Promise<string>;
}

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */
export const AuthContext = createContext<AuthContextShape>({} as AuthContextShape);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}

/**
 * Get the tenant fetchJson sends as x-tenant-id. Nothing while signed out, so the sign-in session
 * request falls back to the cookie and its single-tenant default instead of a leftover ALL.
 *
 * Non-dashboard users are Greenhouse-only technicians, and effectiveTenantId is always ALL for them.
 * Before fetchJson sent effectiveTenantId as x-tenant-id by default, a request without a tenantId
 * param fell back to the cookie. For technicians with a single tenant, the cookie held that tenant,
 * not ALL. Keep sending that tenant for them.
 */
export function getFetchTenantId({
  user,
  isNonDashboardUser,
  effectiveTenantId,
}: {
  user: Pick<User, "tenantPermissions"> | null | undefined;
  isNonDashboardUser: boolean;
  effectiveTenantId: string | null;
}): string | null {
  if (!user) {
    return null;
  }
  const permittedTenantIds = Object.keys(user.tenantPermissions);
  return isNonDashboardUser && permittedTenantIds.length === 1
    ? permittedTenantIds[0]!
    : effectiveTenantId;
}

/* ------------------------------------------------------------------ */
/* Provider                                                            */
/* ------------------------------------------------------------------ */
export default function AuthProvider({ children }: { children: React.ReactNode }) {
  // useMeQuery calls useSearchParams (to refetch on `?spoof=` changes), and
  // useSearchParams bails out static prerender unless wrapped in a Suspense
  // boundary. Move the hook usage into an inner component that lives behind
  // the boundary.
  return (
    <Suspense fallback={<Loader variant="fullscreen" />}>
      <AuthProviderInner>{children}</AuthProviderInner>
    </Suspense>
  );
}

function AuthProviderInner({ children }: { children: React.ReactNode }) {
  /* ---------- local state ---------- */
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [logoutReason, setLogoutReason] = useState<LogoutReason>(null);

  /* ---------- hooks ---------- */
  const meQuery = useMeQuery();

  const signInMut = useSignIn();
  const signOutMut = useSignOut(() => setLogoutReason("manual"));

  const { mutateAsync: selectTenant, isPending: isSwitchingTenant } =
    useSelectTenant(setActiveTenantId);
  const switchTenant = useCallback(
    (id: string, opts?: { redirectTo?: string; refresh?: boolean }) =>
      selectTenant({ tenantId: id, redirectTo: opts?.redirectTo, refresh: opts?.refresh }),
    [selectTenant]
  );

  /* ------------------------------------------------------------------
   * Clear logout-reason after a successful auth refresh
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (meQuery.data?.user) {
      setLogoutReason(null);
    }
  }, [meQuery.data?.user]);

  /* ------------------------------------------------------------------
   * Mark session-expired on 401 responses
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (meQuery.isError && isHttpError(meQuery.error) && meQuery.error.status === 401) {
      setLogoutReason("session-expired");
    }
  }, [meQuery.isError, meQuery.error]);

  /* ------------------------------------------------------------------
   * Derive the effective tenant ID
   * ------------------------------------------------------------------ */
  const serverActiveId = meQuery.data?.activeTenantId as typeof ALL_TENANTS_ID | string | null;
  const isSelfServe = meQuery.data?.isSelfServe ?? false;
  const tenants = meQuery.data?.tenants ?? [];
  const user = meQuery.data?.user;

  // Non-dashboard users (Greenhouse-only, e.g. technicians via SSO) must always
  // be in all-tenants mode so the greenhouse layout's all-tenants guard allows access.
  const isNonDashboardUser =
    user &&
    !user.superAdmin &&
    Object.values(user.tenantPermissions).length > 0 &&
    Object.values(user.tenantPermissions).every(p => p.role === "non_dashboard_user");

  // Users whose roles show no everyday pages but do show analytics land on analytics on sign-in.
  const isAnalyticsViewer =
    user &&
    !user.superAdmin &&
    !isNonDashboardUser &&
    !canAny(user, "inbox.view") &&
    canAny(user, "analytics.view");

  // Self-serve users should never be in multi-tenant mode (ALL_TENANTS_ID).
  // For self-serve users with multiple tenants, default to the first tenant.
  // Non-dashboard users (e.g. technicians) always need ALL_TENANTS_ID — this
  // must override serverActiveId which the session API may set to a specific tenant.
  const effectiveTenantId: string | null = !meQuery.isFetched
    ? null
    : isNonDashboardUser
      ? ALL_TENANTS_ID
      : (activeTenantId ??
        serverActiveId ??
        (tenants.length === 1
          ? tenants[0]!.id
          : isSelfServe
            ? (tenants[0]?.id ?? ALL_TENANTS_ID)
            : ALL_TENANTS_ID));

  const fetchTenantId = getFetchTenantId({
    user,
    isNonDashboardUser: !!isNonDashboardUser,
    effectiveTenantId,
  });

  // Layout effect so the header is set before children's queries fire.
  useLayoutEffect(() => {
    setActiveTenantIdForFetch(fetchTenantId);
  }, [fetchTenantId]);

  /* ------------------------------------------------------------------
   * Auto-switch to valid tenant if current one is not allowed
   * ------------------------------------------------------------------ */
  useEffect(() => {
    const user = meQuery.data?.user;
    const tenants = meQuery.data?.tenants ?? [];

    if (!user || !effectiveTenantId || effectiveTenantId === ALL_TENANTS_ID || !meQuery.isFetched) {
      return;
    }

    // Check if user has permission for the current tenant
    const hasPermission = user.superAdmin || getRoleForTenant(user, effectiveTenantId) !== null;

    if (!hasPermission) {
      // Find first tenant the user has permissions for
      const allowedTenant = tenants.find(
        tenant => user.superAdmin || getRoleForTenant(user, tenant.id) !== null
      );

      if (allowedTenant) {
        // Auto-switch to the first allowed tenant
        void switchTenant(allowedTenant.id);
      } else if (tenants.length > 1) {
        // If no specific tenant is allowed but there are multiple, switch to "All Tenants"
        void switchTenant(ALL_TENANTS_ID);
      }
    }
  }, [
    meQuery.data?.user,
    meQuery.data?.tenants,
    effectiveTenantId,
    meQuery.isFetched,
    switchTenant,
  ]);

  /* ------------------------------------------------------------------
   * Stable function references
   * ------------------------------------------------------------------ */
  const signInFn = signInMut.mutateAsync;
  const signOutFn = signOutMut.mutateAsync;
  const switchTenantFn = switchTenant;

  /* ---------- memoized context ---------- */
  const ctx = useMemo<AuthContextShape>(
    () => ({
      user: meQuery.data?.user ?? null,
      tenants: meQuery.data?.tenants ?? [],
      activeTenantId: effectiveTenantId,
      authResolved: meQuery.isFetched,
      isAuthenticated: !!meQuery.data?.user,
      isSwitchingTenant,
      isSelfServe: meQuery.data?.isSelfServe ?? false,
      isAccountOverride: meQuery.data?.isAccountOverride ?? false,
      piiDemoBlinding: meQuery.data?.piiDemoBlinding ?? false,
      isNonDashboardUser: !!isNonDashboardUser,
      isAnalyticsViewer: !!isAnalyticsViewer,
      isSpoofing: meQuery.data?.isSpoofing ?? false,
      realUser: meQuery.data?.realUser ?? null,
      spoofError: meQuery.data?.spoofError ?? null,
      logoutReason,

      signIn: signInFn,
      signOut: signOutFn,
      switchTenant: switchTenantFn,
    }),
    [
      meQuery.data,
      meQuery.isFetched,
      effectiveTenantId,
      isSwitchingTenant,
      logoutReason,
      signInFn,
      signOutFn,
      switchTenantFn,
    ]
  );

  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>;
}
